import React, { useState, useEffect } from 'react';
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  Clock,
  User,
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  Download,
  RefreshCw,
  FileText,
  Users,
  AlertCircle,
  XCircle,
  ArrowRight,
  Globe,
  Scale,
  Building2,
  Briefcase,
  Target,
  MapPin,
  Calendar,
  Link2,
  ExternalLink,
  Info,
  Layers,
  GitBranch
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

// Due Diligence Framework Metadata
const DD_FRAMEWORKS = {
  CSDDD: {
    id: 'CSDDD',
    name: 'CSDDD',
    fullName: 'Corporate Sustainability Due Diligence Directive',
    jurisdiction: 'European Union',
    color: 'purple',
    icon: Shield,
    effectiveDate: '2027-07-26',
    scope: 'EU companies >1000 employees, €450M+ turnover; Non-EU with €450M+ EU turnover',
    keyFeatures: ['Human rights due diligence', 'Environmental due diligence', 'Climate transition plans', 'Civil liability'],
    articles: 23
  },
  LkSG: {
    id: 'LkSG',
    name: 'LkSG',
    fullName: 'Lieferkettensorgfaltspflichtengesetz (German Supply Chain Act)',
    jurisdiction: 'Germany',
    color: 'amber',
    icon: Building2,
    effectiveDate: '2023-01-01',
    scope: 'Companies with >3000 employees (2023), >1000 employees (2024)',
    keyFeatures: ['Risk management system', 'Preventive measures', 'Remedial actions', 'Complaints procedure'],
    articles: 13
  },
  FRENCH_VIGILANCE: {
    id: 'FRENCH_VIGILANCE',
    name: 'French Vigilance Law',
    fullName: 'Loi de Vigilance (Duty of Vigilance Law)',
    jurisdiction: 'France',
    color: 'blue',
    icon: Scale,
    effectiveDate: '2017-03-27',
    scope: 'French companies with >5000 employees in France or >10000 worldwide',
    keyFeatures: ['Vigilance plan', 'Risk mapping', 'Mitigation actions', 'Alert mechanism'],
    articles: 7
  },
  DUTCH_CSDDD: {
    id: 'DUTCH_CSDDD',
    name: 'Dutch CSDDD',
    fullName: 'Wet verantwoord en duurzaam internationaal ondernemen',
    jurisdiction: 'Netherlands',
    color: 'orange',
    icon: Globe,
    effectiveDate: '2024-01-01',
    scope: 'Large companies selling goods/services in the Netherlands',
    keyFeatures: ['Due diligence policy', 'Risk assessment', 'Action plan', 'Monitoring'],
    articles: 10
  },
  NORWEGIAN_TRANSPARENCY: {
    id: 'NORWEGIAN_TRANSPARENCY',
    name: 'Norwegian Transparency Act',
    fullName: 'Åpenhetsloven (Transparency Act)',
    jurisdiction: 'Norway',
    color: 'teal',
    icon: FileText,
    effectiveDate: '2022-07-01',
    scope: 'Large enterprises in Norway',
    keyFeatures: ['Due diligence assessments', 'Right to information', 'Annual reporting', 'Remediation'],
    articles: 14
  }
};

// Due Diligence Steps (common across frameworks)
const DD_STEPS = [
  { id: 1, name: 'Embed Responsible Business Conduct', description: 'Integrate due diligence into policies and management systems', icon: Building2 },
  { id: 2, name: 'Identify & Assess Adverse Impacts', description: 'Map value chain and identify actual/potential human rights & environmental impacts', icon: Search },
  { id: 3, name: 'Cease, Prevent, Mitigate', description: 'Take action to address identified adverse impacts', icon: Shield },
  { id: 4, name: 'Track Implementation', description: 'Monitor and verify effectiveness of due diligence measures', icon: Target },
  { id: 5, name: 'Communicate', description: 'Report publicly on due diligence policies, processes and findings', icon: FileText },
  { id: 6, name: 'Provide/Cooperate in Remediation', description: 'Enable remediation when company has caused or contributed to adverse impacts', icon: Users }
];

// Status configuration
const STATUS_CONFIG = {
  complete: { color: 'green', icon: CheckCircle2, label: 'Complete', bgClass: 'bg-green-100', textClass: 'text-green-700' },
  in_progress: { color: 'blue', icon: Clock, label: 'In Progress', bgClass: 'bg-blue-100', textClass: 'text-blue-700' },
  at_risk: { color: 'amber', icon: AlertTriangle, label: 'At Risk', bgClass: 'bg-amber-100', textClass: 'text-amber-700' },
  not_started: { color: 'slate', icon: Target, label: 'Not Started', bgClass: 'bg-slate-100', textClass: 'text-slate-700' },
  blocked: { color: 'red', icon: XCircle, label: 'Blocked', bgClass: 'bg-red-100', textClass: 'text-red-700' }
};

// Framework Selector Component
const FrameworkSelector = ({ frameworks, selected, onSelect }) => {
  return (
    <div className="flex flex-wrap gap-2 mb-6">
      <button
        onClick={() => onSelect('all')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          selected === 'all'
            ? 'bg-slate-800 text-white'
            : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-400'
        }`}
      >
        All Frameworks
      </button>
      {Object.values(frameworks).map(fw => {
        const Icon = fw.icon;
        return (
          <button
            key={fw.id}
            onClick={() => onSelect(fw.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              selected === fw.id
                ? `bg-${fw.color}-600 text-white`
                : `bg-white border border-slate-200 text-slate-700 hover:border-${fw.color}-400`
            }`}
          >
            <Icon size={14} />
            {fw.name}
          </button>
        );
      })}
    </div>
  );
};

// Framework Overview Card
const FrameworkOverviewCard = ({ framework }) => {
  const Icon = framework.icon;
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className={`bg-white rounded-xl border border-${framework.color}-200 p-4 hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-${framework.color}-100 flex items-center justify-center`}>
            <Icon className={`text-${framework.color}-600`} size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{framework.name}</h3>
            <p className="text-xs text-slate-500">{framework.jurisdiction}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium bg-${framework.color}-100 text-${framework.color}-700`}>
          {framework.articles} Articles
        </span>
      </div>
      
      <p className="text-sm text-slate-600 mb-3">{framework.fullName}</p>
      
      <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
        <span className="flex items-center gap-1">
          <Calendar size={12} />
          Effective: {framework.effectiveDate}
        </span>
        <span className="flex items-center gap-1">
          <MapPin size={12} />
          {framework.jurisdiction}
        </span>
      </div>
      
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-xs text-slate-600 flex items-center gap-1 hover:text-slate-900"
      >
        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {isExpanded ? 'Hide details' : 'Show scope & features'}
      </button>
      
      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          <div>
            <p className="text-xs font-medium text-slate-700 mb-1">Scope:</p>
            <p className="text-xs text-slate-600">{framework.scope}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-700 mb-1">Key Features:</p>
            <div className="flex flex-wrap gap-1">
              {framework.keyFeatures.map((feature, idx) => (
                <span key={idx} className={`px-2 py-0.5 rounded-full text-xs bg-${framework.color}-50 text-${framework.color}-700`}>
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Due Diligence Step Card
const DDStepCard = ({ step, obligations, onStatusChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = step.icon;
  
  const stepObligations = obligations.filter(o => o.dd_step === step.id);
  const completedCount = stepObligations.filter(o => o.status === 'complete').length;
  const totalCount = stepObligations.length;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold">
            {step.id}
          </div>
          <div className="text-left">
            <h4 className="font-medium text-slate-900">{step.name}</h4>
            <p className="text-xs text-slate-500">{step.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-slate-900">{completedCount}/{totalCount}</p>
            <p className="text-xs text-slate-500">obligations</p>
          </div>
          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        </div>
      </button>
      
      {isExpanded && stepObligations.length > 0 && (
        <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-2">
          {stepObligations.map(obligation => {
            const status = STATUS_CONFIG[obligation.status] || STATUS_CONFIG.not_started;
            const StatusIcon = status.icon;
            const framework = DD_FRAMEWORKS[obligation.framework];
            
            return (
              <div key={obligation.id} className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${framework?.color || 'slate'}-100 text-${framework?.color || 'slate'}-700`}>
                        {obligation.framework}
                      </span>
                      {obligation.article && (
                        <span className="text-xs text-slate-500">Article {obligation.article}</span>
                      )}
                    </div>
                    <h5 className="font-medium text-slate-900 text-sm">{obligation.title}</h5>
                    <p className="text-xs text-slate-600 mt-1">{obligation.description}</p>
                  </div>
                  <select
                    value={obligation.status}
                    onChange={(e) => onStatusChange(obligation.id, e.target.value)}
                    className={`ml-3 px-2 py-1 rounded text-xs font-medium ${status.bgClass} ${status.textClass} border-0 cursor-pointer`}
                  >
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {isExpanded && stepObligations.length === 0 && (
        <div className="border-t border-slate-100 p-4 bg-slate-50">
          <p className="text-sm text-slate-500 text-center">No obligations for this step in selected framework</p>
        </div>
      )}
    </div>
  );
};

// Inter-Module Intelligence Panel
const InterModulePanel = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const connections = [
    { module: 'CSDDD Command Center', path: '/csddd', description: 'Create assessments, identify risks, document remediation', icon: Shield, color: 'purple' },
    { module: 'Value Chain Risk', path: '/value-chain', description: 'Map suppliers, assess country risks, track engagement', icon: Globe, color: 'blue' },
    { module: 'Claims & Assertions', path: '/claims-assertions', description: 'Make substantiated claims based on due diligence', icon: FileText, color: 'emerald' },
    { module: 'Audit Pack Builder', path: '/audit-pack', description: 'Compile evidence for regulatory audits', icon: Briefcase, color: 'amber' }
  ];
  
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between mb-3"
      >
        <h3 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
          <GitBranch size={16} className="text-indigo-600" />
          Connected Modules
          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">Data Flows</span>
        </h3>
        {isExpanded ? <ChevronDown size={16} className="text-indigo-600" /> : <ChevronRight size={16} className="text-indigo-600" />}
      </button>
      
      {isExpanded && (
        <div className="grid grid-cols-2 gap-3">
          {connections.map((conn, idx) => {
            const Icon = conn.icon;
            return (
              <a
                key={idx}
                href={conn.path}
                className="bg-white/80 rounded-lg p-3 border border-indigo-100 hover:border-indigo-300 transition-colors flex items-start gap-3"
              >
                <div className={`w-8 h-8 rounded-lg bg-${conn.color}-100 flex items-center justify-center flex-shrink-0`}>
                  <Icon size={16} className={`text-${conn.color}-600`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-slate-900 text-sm flex items-center gap-1">
                    {conn.module}
                    <ExternalLink size={10} className="text-slate-400" />
                  </h4>
                  <p className="text-xs text-slate-600">{conn.description}</p>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main Component
const DueDiligenceObligations = () => {
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [obligations, setObligations] = useState([]);
  const [crosswalks, setCrosswalks] = useState([]);
  const [crosswalkSummary, setCrosswalkSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('steps'); // 'steps', 'frameworks', 'list', 'crosswalks'
  const [showGuide, setShowGuide] = useState(true);
  
  // Fetch obligations from API
  useEffect(() => {
    const fetchObligations = async () => {
      setLoading(true);

      const params = new URLSearchParams();
      if (selectedFramework !== 'all') params.set('framework', selectedFramework);
      const query = params.toString() ? `?${params}` : '';
      const data = await apiFetch(`${API}/api/due-diligence/obligations${query}`);

      if (data?._error) {
        console.error('Error fetching obligations:', data.message);
      } else if (data.success && data.obligations) {
        setObligations(data.obligations);
      }
      setLoading(false);
    };
    
    fetchObligations();
  }, [selectedFramework]);
  
  // Fetch crosswalks when on crosswalks tab
  useEffect(() => {
    const fetchCrosswalks = async () => {
      if (activeView !== 'crosswalks') return;
      
      const params = new URLSearchParams();
      if (selectedFramework !== 'all') params.set('framework', selectedFramework);
      const query = params.toString() ? `?${params}` : '';
      const [crosswalksData, summaryData] = await Promise.all([
        apiFetch(`${API}/api/due-diligence/crosswalks${query}`),
        apiFetch(`${API}/api/due-diligence/crosswalks/summary`)
      ]);

      if (crosswalksData?._error || summaryData?._error) {
        console.error('Error fetching crosswalks:', crosswalksData?._error ? crosswalksData.message : summaryData.message);
        return;
      }
      if (crosswalksData.success) {
        setCrosswalks(crosswalksData.crosswalks || []);
      }
      if (summaryData.success) {
        setCrosswalkSummary(summaryData);
      }
    };
    
    fetchCrosswalks();
  }, [activeView, selectedFramework]);
  
  // Generate default obligations
  const generateDefaultObligations = () => {
    const obligations = [];
    
    // CSDDD Obligations
    const csdddObligations = [
      { dd_step: 1, article: '5', title: 'Integration into Policies', description: 'Integrate due diligence into all relevant corporate policies and risk management systems' },
      { dd_step: 1, article: '5', title: 'Code of Conduct', description: 'Develop and adopt a code of conduct describing rules and principles for employees and subsidiaries' },
      { dd_step: 2, article: '6', title: 'Risk Identification', description: 'Identify actual and potential adverse human rights and environmental impacts' },
      { dd_step: 2, article: '6', title: 'Value Chain Mapping', description: 'Map the company\'s own operations, subsidiaries, and business relationships in the chain of activities' },
      { dd_step: 3, article: '7', title: 'Prevention of Potential Impacts', description: 'Take appropriate measures to prevent or adequately mitigate potential adverse impacts' },
      { dd_step: 3, article: '8', title: 'Bringing Actual Impacts to an End', description: 'Take appropriate measures to bring actual adverse impacts to an end' },
      { dd_step: 4, article: '10', title: 'Monitoring Effectiveness', description: 'Carry out periodic assessments of own operations and measures to verify implementation and monitor adequacy' },
      { dd_step: 5, article: '14', title: 'Annual Reporting', description: 'Publish annual statement on due diligence website describing impacts and measures taken' },
      { dd_step: 6, article: '12', title: 'Remediation', description: 'Provide remediation to persons and communities affected by adverse impacts the company caused or contributed to' },
      { dd_step: 6, article: '9', title: 'Complaints Procedure', description: 'Establish and maintain a complaints procedure for stakeholders to submit concerns' },
      { dd_step: 1, article: '15', title: 'Climate Transition Plan', description: 'Adopt and put into effect a transition plan for climate change mitigation aligned with Paris Agreement' }
    ];
    
    csdddObligations.forEach((o, idx) => {
      obligations.push({
        id: `csddd-${idx}`,
        framework: 'CSDDD',
        ...o,
        status: ['complete', 'in_progress', 'not_started', 'at_risk'][Math.floor(Math.random() * 4)]
      });
    });
    
    // LkSG (German Supply Chain Act) Obligations
    const lksgObligations = [
      { dd_step: 1, article: '3', title: 'Risk Management System', description: 'Establish and implement an effective risk management system' },
      { dd_step: 1, article: '6(2)', title: 'Human Rights Officer', description: 'Designate a person responsible for monitoring risk management (Human Rights Officer)' },
      { dd_step: 2, article: '5', title: 'Risk Analysis', description: 'Conduct annual and ad-hoc risk analysis of human rights and environmental risks' },
      { dd_step: 3, article: '6', title: 'Preventive Measures', description: 'Implement appropriate preventive measures based on risk analysis findings' },
      { dd_step: 3, article: '7', title: 'Remedial Actions', description: 'Take remedial action if a violation of duty has already occurred' },
      { dd_step: 6, article: '8', title: 'Complaints Procedure', description: 'Establish a complaints procedure enabling persons to report human rights or environmental risks' },
      { dd_step: 4, article: '4(3)', title: 'Effectiveness Review', description: 'Review effectiveness of preventive and remedial measures annually and on ad-hoc basis' },
      { dd_step: 5, article: '10', title: 'Documentation and Reporting', description: 'Document fulfillment of due diligence obligations and publish annual report' }
    ];
    
    lksgObligations.forEach((o, idx) => {
      obligations.push({
        id: `lksg-${idx}`,
        framework: 'LkSG',
        ...o,
        status: ['complete', 'in_progress', 'not_started'][Math.floor(Math.random() * 3)]
      });
    });
    
    // French Vigilance Law Obligations
    const frenchObligations = [
      { dd_step: 1, article: '1', title: 'Vigilance Plan', description: 'Establish, implement and publish a vigilance plan' },
      { dd_step: 2, article: '1(1)', title: 'Risk Mapping', description: 'Create a risk map identifying, analyzing and prioritizing risks' },
      { dd_step: 3, article: '1(2)', title: 'Assessment Procedures', description: 'Implement procedures for regular assessment of subsidiaries, subcontractors and suppliers' },
      { dd_step: 3, article: '1(3)', title: 'Mitigation Actions', description: 'Take appropriate actions to mitigate risks or prevent serious violations' },
      { dd_step: 6, article: '1(4)', title: 'Alert Mechanism', description: 'Establish an alert mechanism to collect reports of risks from stakeholders' },
      { dd_step: 4, article: '1(5)', title: 'Monitoring System', description: 'Implement a monitoring system to track measures and assess their effectiveness' },
      { dd_step: 5, article: '2', title: 'Public Reporting', description: 'Include vigilance plan and implementation report in annual management report' }
    ];
    
    frenchObligations.forEach((o, idx) => {
      obligations.push({
        id: `french-${idx}`,
        framework: 'FRENCH_VIGILANCE',
        ...o,
        status: ['complete', 'in_progress', 'not_started'][Math.floor(Math.random() * 3)]
      });
    });
    
    // Norwegian Transparency Act Obligations
    const norwegianObligations = [
      { dd_step: 2, article: '4', title: 'Due Diligence Assessments', description: 'Carry out due diligence assessments in accordance with OECD Guidelines' },
      { dd_step: 5, article: '5', title: 'Account of Due Diligence', description: 'Publish an account of due diligence assessments annually' },
      { dd_step: 6, article: '6', title: 'Right to Information', description: 'Respond to information requests from any person regarding due diligence' },
      { dd_step: 3, article: '4(c)', title: 'Cease and Mitigate', description: 'Cease, prevent or mitigate negative impacts based on assessments' },
      { dd_step: 4, article: '4(e)', title: 'Track Implementation', description: 'Track implementation and results of measures to address negative impacts' }
    ];
    
    norwegianObligations.forEach((o, idx) => {
      obligations.push({
        id: `norwegian-${idx}`,
        framework: 'NORWEGIAN_TRANSPARENCY',
        ...o,
        status: ['complete', 'in_progress', 'not_started'][Math.floor(Math.random() * 3)]
      });
    });
    
    return obligations;
  };
  
  // Update obligation status
  const handleStatusChange = async (obligationId, newStatus) => {
    // Update local state first for immediate feedback
    setObligations(prev => prev.map(o => 
      (o.id === obligationId || o.obligation_id === obligationId) ? { ...o, status: newStatus } : o
    ));
    
    // Persist to API
    const data = await apiFetch(`${API}/api/due-diligence/obligations/${obligationId}`, {
      method: 'PUT',
      body: { status: newStatus }
    });
    if (data?._error) {
      console.error('Status update failed:', data.message);
    } else if (!data.success) {
      console.error('Failed to update status');
    }
  };
  
  // Filter obligations
  const filteredObligations = obligations.filter(o => {
    const matchesFramework = selectedFramework === 'all' || o.framework === selectedFramework;
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    const matchesSearch = !searchTerm || 
      o.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFramework && matchesStatus && matchesSearch;
  });
  
  // Calculate stats
  const stats = {
    total: filteredObligations.length,
    complete: filteredObligations.filter(o => o.status === 'complete').length,
    in_progress: filteredObligations.filter(o => o.status === 'in_progress').length,
    at_risk: filteredObligations.filter(o => o.status === 'at_risk').length,
    not_started: filteredObligations.filter(o => o.status === 'not_started').length
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="text-purple-600" size={28} />
            Due Diligence Obligations Tracker
          </h1>
          <p className="text-slate-600 mt-1">
            Track compliance with CSDDD, LkSG, French Vigilance Law, and other due diligence frameworks
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2">
            <Download size={16} />
            Export
          </button>
          <button className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-2">
            <RefreshCw size={16} />
            Sync Status
          </button>
        </div>
      </div>
      
      {/* Quick Start Guide */}
      {showGuide && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <Info className="text-purple-600 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-purple-900 mb-1">Due Diligence Obligations vs Disclosure Requirements</h3>
                <p className="text-sm text-purple-800 mb-2">
                  This tracker focuses on <strong>ACTION-based obligations</strong> — what you must <strong>DO</strong> to comply with due diligence laws.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">Risk Management Systems</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">Preventive Measures</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">Remediation Actions</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">Complaints Procedures</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">Climate Transition Plans</span>
                </div>
              </div>
            </div>
            <button onClick={() => setShowGuide(false)} className="text-purple-400 hover:text-purple-600">
              <XCircle size={20} />
            </button>
          </div>
        </div>
      )}
      
      {/* Inter-Module Intelligence */}
      <InterModulePanel />
      
      {/* Stats Bar */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { label: 'Total Obligations', value: stats.total, color: 'slate', icon: Target },
          { label: 'Complete', value: stats.complete, color: 'green', icon: CheckCircle2 },
          { label: 'In Progress', value: stats.in_progress, color: 'blue', icon: Clock },
          { label: 'At Risk', value: stats.at_risk, color: 'amber', icon: AlertTriangle },
          { label: 'Not Started', value: stats.not_started, color: 'slate', icon: AlertCircle }
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className={`bg-white rounded-xl border border-slate-200 p-4`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className={`text-${stat.color}-500`} size={20} />
                <span className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</span>
              </div>
              <p className="text-xs text-slate-600">{stat.label}</p>
            </div>
          );
        })}
      </div>
      
      {/* Framework Selector */}
      <FrameworkSelector 
        frameworks={DD_FRAMEWORKS} 
        selected={selectedFramework} 
        onSelect={setSelectedFramework} 
      />
      
      {/* View Toggle & Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
          {[
            { id: 'steps', label: 'By DD Steps', icon: Layers },
            { id: 'frameworks', label: 'By Framework', icon: Globe },
            { id: 'crosswalks', label: 'Cross-Mappings', icon: Link2 },
            { id: 'list', label: 'List View', icon: FileText }
          ].map(view => {
            const Icon = view.icon;
            return (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                  activeView === view.id
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon size={14} />
                {view.label}
              </button>
            );
          })}
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search obligations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Content based on view */}
      {activeView === 'steps' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Layers size={20} className="text-purple-600" />
            6-Step Due Diligence Process (OECD Guidelines)
          </h2>
          {DD_STEPS.map(step => (
            <DDStepCard 
              key={step.id} 
              step={step} 
              obligations={filteredObligations}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}
      
      {activeView === 'frameworks' && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Globe size={20} className="text-purple-600" />
            Due Diligence Frameworks Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.values(DD_FRAMEWORKS).map(framework => (
              <FrameworkOverviewCard key={framework.id} framework={framework} />
            ))}
          </div>
          
          <h3 className="text-md font-semibold text-slate-800 mt-6">Obligations by Framework</h3>
          {Object.values(DD_FRAMEWORKS).map(framework => {
            const frameworkObligations = filteredObligations.filter(o => o.framework === framework.id);
            if (frameworkObligations.length === 0) return null;
            
            return (
              <div key={framework.id} className="bg-white rounded-xl border border-slate-200 p-4">
                <h4 className={`font-medium text-${framework.color}-700 mb-3 flex items-center gap-2`}>
                  <framework.icon size={16} />
                  {framework.name} ({frameworkObligations.length} obligations)
                </h4>
                <div className="space-y-2">
                  {frameworkObligations.map(obligation => {
                    const status = STATUS_CONFIG[obligation.status] || STATUS_CONFIG.not_started;
                    return (
                      <div key={obligation.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{obligation.title}</p>
                          <p className="text-xs text-slate-500">Article {obligation.article}</p>
                        </div>
                        <select
                          value={obligation.status}
                          onChange={(e) => handleStatusChange(obligation.id, e.target.value)}
                          className={`px-2 py-1 rounded text-xs font-medium ${status.bgClass} ${status.textClass} border-0`}
                        >
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <option key={key} value={key}>{config.label}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {activeView === 'list' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Framework</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Article</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Obligation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">DD Step</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredObligations.map(obligation => {
                const status = STATUS_CONFIG[obligation.status] || STATUS_CONFIG.not_started;
                const framework = DD_FRAMEWORKS[obligation.framework];
                const step = DD_STEPS.find(s => s.id === obligation.dd_step);
                
                return (
                  <tr key={obligation.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium bg-${framework?.color || 'slate'}-100 text-${framework?.color || 'slate'}-700`}>
                        {obligation.framework}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">Art. {obligation.article}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-900">{obligation.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{obligation.description?.substring(0, 80)}...</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-600">{step?.name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={obligation.status}
                        onChange={(e) => handleStatusChange(obligation.obligation_id || obligation.id, e.target.value)}
                        className={`px-2 py-1 rounded text-xs font-medium ${status.bgClass} ${status.textClass} border-0`}
                      >
                        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                          <option key={key} value={key}>{config.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Cross-Framework Mappings View */}
      {activeView === 'crosswalks' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Link2 size={20} className="text-purple-600" />
              Cross-Framework Mappings
            </h2>
            {crosswalkSummary && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-600">
                  <strong>{crosswalkSummary.total_mappings}</strong> mappings across <strong>{crosswalkSummary.frameworks_mapped}</strong> frameworks
                </span>
              </div>
            )}
          </div>
          
          {/* Crosswalk explanation */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
            <p className="text-sm text-purple-800">
              <strong>Cross-framework mappings</strong> show how obligations in one regulation relate to obligations in another. 
              This helps you identify overlapping requirements and streamline compliance efforts across multiple jurisdictions.
            </p>
          </div>
          
          {/* Crosswalk strength legend */}
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-slate-600">Strong alignment</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-amber-500"></span>
              <span className="text-slate-600">Moderate alignment</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-slate-400"></span>
              <span className="text-slate-600">Weak alignment</span>
            </span>
          </div>
          
          {/* Crosswalks list */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">From Framework</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Article</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Alignment</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">To Framework</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Article</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Topic</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {crosswalks.map((cw, idx) => {
                  const fromFramework = DD_FRAMEWORKS[cw.from_framework];
                  const toFramework = DD_FRAMEWORKS[cw.to_framework];
                  const strengthColor = cw.strength === 'strong' ? 'green' : cw.strength === 'moderate' ? 'amber' : 'slate';
                  
                  return (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium bg-${fromFramework?.color || 'slate'}-100 text-${fromFramework?.color || 'slate'}-700`}>
                          {cw.from_framework}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">Art. {cw.from_article}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <ArrowRight size={14} className={`text-${strengthColor}-500`} />
                          <span className={`w-2 h-2 rounded-full bg-${strengthColor}-500`}></span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium bg-${toFramework?.color || 'slate'}-100 text-${toFramework?.color || 'slate'}-700`}>
                          {cw.to_framework}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">Art. {cw.to_article}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-700">{cw.topic}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {crosswalks.length === 0 && (
              <div className="p-8 text-center">
                <Link2 size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">No cross-framework mappings found for selected framework</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DueDiligenceObligations;
